import React from 'react';
import { getLoggedUser } from '../../../../../../state/user';
import { getPaymentMethods } from '../../../../../../models/thawani';
import i18n from '../../../../../../i18n';

import logoThawani from '../../../../../../logoThawani.svg';
import {
  Building2,
  Wallet,
  Upload,
  FileText,
  AlertCircle,
  Loader,
  Loader2,
  Check,
  ScanLine,
  X,
  CreditCard,
} from 'lucide-react';
import '../../../../../../styles/components/enroll-page.scss';
import { toast } from 'react-toastify';
import { scanReceipt } from '../../../../../home/pages/Enroll/services/enrollService';
import { convertPdfToImage } from '../../../../../../utils/pdfToImage';
import { initiateWalletRecharge, walletRecharge } from '../../services/walletService';
import SmartPayRedirectForm from '../../../Payment/SmartPayRedirectForm';

const user = getLoggedUser();

const MAX_FILE_SIZE_KB = 5120;

function isFileSizeValid(file) {
  return file.size / 1024 <= MAX_FILE_SIZE_KB;
}

export class RechargeForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      // Payment
      paymentMethod: 'offline',
      onlineType: 'local',

      // Form
      formInput: {
        recharge_amount: '',
        bank: '',
        account_number: '',
        receipt_number: '',
        transfer_date: '',
      },
      fieldErrors: {},

      // Receipt upload
      receiptFile: null,
      receiptPreviewUrl: null,
      receiptFileError: null,
      isDraggingOver: false,

      // Saved cards
      cards: [],
      isCardsLoading: false,

      // Loading
      isLoading: false,
      isPaymentLoading: false,
    };

    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleSaveFund = this.handleSaveFund.bind(this);
  }

  componentWillUnmount() {
    if (this.state.receiptPreviewUrl) {
      URL.revokeObjectURL(this.state.receiptPreviewUrl);
    }
  }

  // ─── Handlers ──────────────────────────────────────────────

  handleInputChange(e) {
    const { name, value } = e.target;
    this.setState((prev) => ({
      formInput: { ...prev.formInput, [name]: value },
      fieldErrors: { ...prev.fieldErrors, [name]: undefined },
    }));
  }

  handlePaymentMethodChange(method) {
    this.setState({ paymentMethod: method });
    if (method === 'online') {
      this.loadSavedCards();
    }
  }

  // ─── File Handling ─────────────────────────────────────────

  handleDragOver = (e) => {
    e.preventDefault();
    this.setState({ isDraggingOver: true });
  };

  handleDragLeave = () => {
    this.setState({ isDraggingOver: false });
  };

  handleDrop = (e) => {
    e.preventDefault();
    this.setState({ isDraggingOver: false });
    this.processReceiptFile(e.dataTransfer.files[0]);
  };

  handleFileChange = (e) => {
    this.processReceiptFile(e.target.files[0]);
  };

  processReceiptFile(file) {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      this.setState({ receiptFileError: 'Invalid file type. Use JPG, PNG or PDF.' });
      return;
    }

    if (!isFileSizeValid(file)) {
      this.setState({ receiptFileError: 'File too large. Maximum allowed size is 5 MB.' });
      return;
    }

    if (this.state.receiptPreviewUrl) {
      URL.revokeObjectURL(this.state.receiptPreviewUrl);
    }

    const previewUrl = URL.createObjectURL(file);
    this.setState({
      receiptFile: file,
      receiptPreviewUrl: previewUrl,
      receiptFileError: null,
      fieldErrors: { ...this.state.fieldErrors, receiptFile: undefined },
    });
  }

  removeReceiptFile = () => {
    if (this.state.receiptPreviewUrl) {
      URL.revokeObjectURL(this.state.receiptPreviewUrl);
    }
    this.setState({ receiptFile: null, receiptPreviewUrl: null, receiptFileError: null });
  };

  openReceiptPreview = () => {
    if (this.state.receiptPreviewUrl) {
      window.open(this.state.receiptPreviewUrl, '_blank');
    }
  };

  formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return Math.round(bytes / 1024) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  }

  // ─── API Calls ─────────────────────────────────────────────

  async loadSavedCards() {
    if (!user.thawani_id) return;
    this.setState({ isCardsLoading: true });
    try {
      const response = await getPaymentMethods(user.thawani_id);
      if (response.success) {
        this.setState({ cards: response.data });
      }
    } catch (error) {
      console.error('Error fetching saved cards:', error);
    } finally {
      this.setState({ isCardsLoading: false });
    }
  }

  async handleSaveFund(e) {
    e.preventDefault();
    const { formInput, receiptFile } = this.state;
    const fieldErrors = {};

    const isEmpty = (value) => value == null || value === 'null' || String(value).trim() === '';

    const receiptNumber = formInput.receipt_number?.trim();
    const bank = formInput.bank?.trim();
    const accountNumber = formInput.account_number?.trim();

    // ── Frontend validation ──────────────────────────────
    if (isEmpty(formInput.recharge_amount)) {
      fieldErrors.recharge_amount = i18n.t('field_required');
    }

    if (isEmpty(formInput.transfer_date)) {
      fieldErrors.transfer_date = i18n.t('field_required');
    }

    if (isEmpty(receiptNumber)) {
      fieldErrors.receipt_number = i18n.t('field_required');
    } else if (!/^[A-Za-z0-9!@#$%^&*()_\-+=[\]{}:;"'<>?,./]{4,}$/.test(receiptNumber)) {
      fieldErrors.receipt_number = i18n.t('receipt_min_4_chars');
    }

    if (isEmpty(bank)) {
      fieldErrors.bank = i18n.t('field_required');
    }

    if (isEmpty(accountNumber)) {
      fieldErrors.account_number = i18n.t('field_required');
    }

    if (!receiptFile) {
      fieldErrors.receiptFile = i18n.t('field_required');
    }

    if (Object.keys(fieldErrors).length > 0) {
      this.setState({ fieldErrors });
      return;
    }

    this.setState({
      isLoading: true,
      fieldErrors: {},
    });

    try {
      const formData = new FormData();
      formData.append('credit', formInput.recharge_amount);
      formData.append('bank', formInput.bank);
      formData.append('account_number', formInput.account_number);
      formData.append('receipt_number', formInput.receipt_number);
      formData.append('payment_date', formInput.transfer_date);
      formData.append('file_receipt', receiptFile, receiptFile.name);

      const response = await walletRecharge(formData);

      // ── Branch 1: success ────────────────────────────────
      if (response?.success) {
        toast.success(i18n.t('fund_added'));
        this.props.navigate('/wallet');
        return;
      }
    } catch (err) {
      // Only truly unexpected errors land here (e.g. JSON parse failure)
      console.error('[handleSaveFund]', err);
      if (Array.isArray(err?.errors) && err.errors.length > 0) {
        const backendErrors = {};
        err.errors.forEach((errorObj) => {
          Object.entries(errorObj).forEach(([field, messages]) => {
            backendErrors[field] = Array.isArray(messages) ? messages[0] : messages;
          });
        });
        this.setState({ fieldErrors: backendErrors });
        return;
      }

      // Otherwise general error toast
      toast.error(err?.message || i18n.t('something_went_wrong'));
    } finally {
      this.setState({ isLoading: false });
    }
  }
  handleOnlineRecharge = async () => {
    const { formInput } = this.state;

    if (!formInput.recharge_amount || Number(formInput.recharge_amount) <= 0) {
      this.setState({ error: 'Enter a valid amount' });
      return;
    }

    this.setState({ loading: true, error: null });

    try {
      const payload = { amount: Number(formInput.recharge_amount) };
      const data = await initiateWalletRecharge(payload);
      console.log(data, 'data');

      this.setState({
        redirectData: {
          actionUrl: data.action_url,
          accessCode: data.access_code,
          encRequest: data.enc_request,
        },
      });
    } catch (err) {
      this.setState({
        loading: false,
        error: err.response?.data?.message || 'Unable to start recharge',
      });
    }
  };
  // async handleGotoPayment(e) {
  //   e.preventDefault();
  //   const { formInput, onlineType } = this.state;

  //   if (!formInput.recharge_amount) {
  //     toast.error('Please enter an amount.');
  //     return;
  //   }

  //   this.setState({ isPaymentLoading: true });

  //   let amount = parseFloat(formInput.recharge_amount);
  //   if (onlineType === 'local') amount += (amount * 1.5) / 100;
  //   if (onlineType === 'credit') amount += (amount * 2) / 100;

  //   const queryParams = `?ptype=online&type=wallet_recharge&amt=${formInput.recharge_amount}`;
  //   const payload = {
  //     client_reference_id: user.email,
  //     mode: 'payment',
  //     products: [
  //       { name: 'Mzadcom Wallet : Recharge', quantity: 1, unit_amount: parseInt(amount) * 1000 },
  //     ],
  //     success_url: walletPaymentSuccessUrl + queryParams,
  //     cancel_url: walletPaymentCancelledUrl,
  //     customer_id: user.thawani_id,
  //     is_save_card_allowed: true,
  //     metadata: { 'Customer name': user.name, 'Order id': user.id + '', user: user.id },
  //   };

  //   const thw = await createThawaniSession(payload);
  //   this.setState({ isPaymentLoading: false });

  //   if (thw?.data?.session_id) {
  //     localStorage.setItem('invoice', thw.data.invoice);
  //     window.location.href = `${thawaniPaymentUrl}${thw.data.session_id}?key=${thawaniConfig.publishable_key}`;
  //   }
  // }

  // ─── Renderers ─────────────────────────────────────────────

  renderAmountField() {
    const { formInput } = this.state;
    return (
      <div className="mze-field">
        <label className="mze-field__label">
          <Wallet size={14} />
          {i18n.t('enter_amount')}
          <span className="mze-field__required">*</span>
        </label>
        <input
          className={`mze-field__input mze-field__input--amount ${this.state.fieldErrors?.['recharge_amount'] ? ' input-error' : ''}`}
          type="number"
          name="recharge_amount"
          placeholder="0.000"
          min="0"
          step="0.001"
          value={formInput.recharge_amount}
          onChange={this.handleInputChange}
          onWheel={(e) => e.target.blur()}
        />
        {this.renderFieldError('recharge_amount')}
      </div>
    );
  }

  renderPaymentTabs() {
    const { paymentMethod } = this.state;
    return (
      <div className="mze-tabs">
        <button
          className={`mze-tab ${paymentMethod === 'offline' ? 'mze-tab--active' : ''}`}
          onClick={() => this.handlePaymentMethodChange('offline')}
        >
          <Building2 size={16} />
          <span>{i18n.t('bank_transfer')}</span>
        </button>
        <button
          className={`mze-tab ${paymentMethod === 'online' ? 'mze-tab--active' : ''}`}
          onClick={() => this.handlePaymentMethodChange('online')}
        >
          <CreditCard size={16} />
          <span>{i18n.t('online')}</span>
        </button>
      </div>
    );
  }

  renderPolicy() {
    return (
      <div className="mze-policy">
        <strong className="mze-policy__title">{i18n.t('online_deposit_and_refund_policy')}</strong>
        <div className="mze-policy__text">
          {[1, 2, 3, 4, 5, 6].map((n) => (
            <p key={n}>{i18n.t(`guarantee_amount_policy_${n}`)}</p>
          ))}
        </div>
      </div>
    );
  }

  renderUploadZone() {
    const { receiptFile, receiptPreviewUrl, receiptFileError, isDraggingOver } = this.state;
    const isPdf = receiptFile?.type === 'application/pdf';
    const isImage = receiptFile?.type?.startsWith('image/');

    return (
      <div className="mze-field mze-field--upload">
        <label className="mze-field__label">
          <Upload size={14} />
          {i18n.t('receipt')}
          <span className="mze-field__required">*</span>
        </label>

        <input
          type="file"
          id="recharge_file_receipt"
          name="file_receipt"
          accept=".jpg,.jpeg,.png,.pdf"
          style={{ display: 'none' }}
          onChange={this.handleFileChange}
        />

        {!receiptFile ? (
          <div
            className={`mze-upload-zone ${isDraggingOver ? 'mze-upload-zone--dragover' : ''}`}
            onDragOver={this.handleDragOver}
            onDragLeave={this.handleDragLeave}
            onDrop={this.handleDrop}
          >
            <div className="mze-upload-zone__icon">
              <Upload size={24} />
            </div>
            <p className="mze-upload-zone__title">{i18n.t('drop_receipt_here')}</p>
            <p className="mze-upload-zone__sub">JPG, PNG or PDF — max 5 MB</p>
            <button
              type="button"
              className="mze-upload-zone__btn"
              onClick={() => document.getElementById('recharge_file_receipt').click()}
            >
              {i18n.t('browse_file')}
            </button>
          </div>
        ) : (
          <div className="mze-receipt-preview">
            <div className="mze-receipt-preview__header">
              <FileText size={15} className="mze-receipt-preview__file-icon" />
              <span className="mze-receipt-preview__name">{receiptFile.name}</span>
              <span className="mze-receipt-preview__size">
                {this.formatFileSize(receiptFile.size)}
              </span>
              <button
                type="button"
                className="mze-receipt-preview__remove"
                onClick={this.removeReceiptFile}
              >
                <X size={12} /> {i18n.t('remove')}
              </button>
            </div>
            <div
              className={`mze-receipt-preview__body ${isImage ? 'mze-receipt-preview__body--image' : ''}`}
            >
              {isImage && (
                <img
                  src={receiptPreviewUrl}
                  alt="Receipt preview"
                  className="mze-receipt-preview__img"
                />
              )}
              {isPdf && (
                <div className="mze-receipt-preview__pdf">
                  <FileText size={32} className="mze-receipt-preview__pdf-icon" />
                  <div>
                    <p className="mze-receipt-preview__pdf-name">{receiptFile.name}</p>
                    <span className="mze-receipt-preview__pdf-meta">
                      PDF document · {this.formatFileSize(receiptFile.size)}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="mze-receipt-preview__actions">
              <button
                type="button"
                className="mze-receipt-preview__view-btn"
                onClick={this.openReceiptPreview}
              >
                <FileText size={13} /> {i18n.t('view_full_size')}
              </button>
              <button
                type="button"
                className="mze-receipt-preview__change-btn"
                onClick={() => document.getElementById('recharge_file_receipt').click()}
              >
                <Upload size={13} /> {i18n.t('change_file')}
              </button>
            </div>
          </div>
        )}

        {receiptFileError && (
          <div className="mze-upload-error">
            <AlertCircle size={13} />
            {receiptFileError}
          </div>
        )}

        {this.renderFieldError('receiptFile')}

        {receiptFile && !receiptFileError && (
          <>
            {/* ── AI Scan Button ── */}
            <div className="mze-scan-row">
              <button
                type="button"
                className={`ai-scan-btn ${this.state.isScanning ? 'ai-scan-btn--scanning' : ''} ${this.state.scanSuccess ? 'ai-scan-btn--success' : ''}`}
                onClick={this.handleReceiptScan}
                disabled={this.state.isScanning || this.state.scanSuccess}
              >
                {this.state.isScanning ? (
                  <>
                    <Loader2 size={16} className="spin" />
                    <span>{i18n.t('scanning_receipt')}</span>
                    <span className="ai-scan-btn__dots">
                      <span />
                      <span />
                      <span />
                    </span>
                  </>
                ) : this.state.scanSuccess ? (
                  <>
                    <Check size={16} />
                    <span>{i18n.t('fields_filled')}</span>
                  </>
                ) : (
                  <>
                    <span className="ai-scan-btn__glow" />
                    <ScanLine size={16} />
                    <span>{i18n.t('auto_fill_from_receipt')}</span>
                    <span className="ai-scan-btn__badge">{i18n.t('ai')}</span>
                  </>
                )}
              </button>
            </div>

            {this.state.scanError && (
              <div className="mze-upload-error" style={{ marginTop: 6 }}>
                <AlertCircle size={13} />
                {this.state.scanError}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  handleReceiptScan = async () => {
    const { receiptFile } = this.state;
    if (!receiptFile) return;

    this.setState({ isScanning: true, scanError: null });

    try {
      let uploadFile = receiptFile;

      // Convert PDF → PNG
      if (receiptFile.type === 'application/pdf') {
        uploadFile = await convertPdfToImage(receiptFile);
      }
      const formData = new FormData();
      formData.append('receipt', uploadFile);

      const result = await scanReceipt(formData);

      if (result.success && result.data) {
        const { bank_name, account_number, receipt_number, amount, transfer_date } =
          result.data.form;

        this.setState((prev) => ({
          formInput: {
            ...prev.formInput,
            bank: bank_name || prev.formInput.bank,
            account_number: account_number || prev.formInput.account_number,
            receipt_number: receipt_number || prev.formInput.receipt_number,
            recharge_amount: amount || prev.formInput.recharge_amount,
            transfer_date: transfer_date || prev.formInput.transfer_date,
          },
          isScanning: false,
          scanSuccess: true,
        }));

        toast.success('Receipt scanned successfully! Please verify the fields.');
      } else {
        this.setState({
          isScanning: false,
          scanError: 'Could not extract data. Please fill manually.',
        });
      }
    } catch (err) {
      console.error('[ReceiptScan]', err);
      this.setState({
        isScanning: false,
        scanError: err.message || 'Scan failed. Please fill the form manually.',
      });
    }
  };

  renderFieldError(field) {
    const msg = this.state.fieldErrors[field];
    if (!msg) return null;
    return (
      <span className="mze-field__error">
        <AlertCircle size={12} />
        {msg}
      </span>
    );
  }

  renderOfflineForm() {
    const { formInput } = this.state;
    return (
      <div className="mze-form-block">
        {this.renderPolicy()}
        {this.renderUploadZone()}

        <div className="mze-offline-fields">
          {/* Transfer Date — first field */}
          <div className="mze-field">
            <label className="mze-field__label">
              <span className="mze-field__required">*</span>
              {i18n.t('transfer_date') || 'Transfer Date'}
            </label>
            <input
              className={`mze-field__input ${this.state.fieldErrors?.['transfer_date'] ? ' input-error' : ''}`}
              type="date"
              name="transfer_date"
              max={new Date().toISOString().split('T')[0]}
              value={formInput.transfer_date}
              onChange={this.handleInputChange}
            />
            {this.renderFieldError('transfer_date')}
          </div>

          {/* Receipt Number */}
          <div className="mze-field">
            <label className="mze-field__label">
              <span className="mze-field__required">*</span>
              {i18n.t('receipt')}
            </label>
            <input
              className={`mze-field__input ${this.state.fieldErrors?.['receipt_number'] ? ' input-error' : ''}`}
              type="text"
              name="receipt_number"
              placeholder={i18n.t('please_enter_receipt_number')}
              value={formInput.receipt_number}
              onChange={this.handleInputChange}
            />
            {this.renderFieldError('receipt_number')}
          </div>
          {/* Bank Name */}
          <div className="mze-field">
            <label className="mze-field__label">
              <span className="mze-field__required">*</span>
              {i18n.t('bank')}
            </label>
            <input
              className={`mze-field__input ${this.state.fieldErrors?.['bank'] ? ' input-error' : ''}`}
              type="text"
              name="bank"
              placeholder={i18n.t('please_enter_bank_name')}
              value={formInput.bank}
              onChange={this.handleInputChange}
            />
            {this.renderFieldError('bank')}
          </div>
          {/* Account Number */}
          <div className="mze-field ">
            <label className="mze-field__label">
              <span className="mze-field__required">*</span>
              {i18n.t('account_number')}
            </label>
            <input
              className={`mze-field__input ${this.state.fieldErrors?.['account_number'] ? ' input-error' : ''}`}
              type="text"
              name="account_number"
              placeholder={i18n.t('please_enter_bank_account')}
              value={formInput.account_number}
              onChange={this.handleInputChange}
            />
            {this.renderFieldError('account_number')}
          </div>
        </div>
      </div>
    );
  }

  renderOnlineForm() {
    return (
      <div className="mze-form-block">
        {this.renderPolicy()}

        <div className="mze-thawani">
          <img src={logoThawani} alt="Thawani" className="mze-thawani__logo" />
          <p className="mze-thawani__text">{i18n.t('redirect_to_thawani')}</p>
        </div>
      </div>
    );
  }

  renderSubmitButton() {
    const { paymentMethod, isLoading, isPaymentLoading } = this.state;
    const isBusy = isLoading || isPaymentLoading;

    return (
      <button
        type="button"
        className="mze-submit"
        disabled={isBusy}
        onClick={paymentMethod === 'offline' ? this.handleSaveFund : this.handleOnlineRecharge}
      >
        {isBusy ? (
          <>
            <Loader size={16} className="mze-submit__spinner" />
            {i18n.t('saving')}
          </>
        ) : (
          i18n.t('submit')
        )}
      </button>
    );
  }

  render() {
    const { paymentMethod, redirectData } = this.state;
    if (redirectData) {
      return (
        <SmartPayRedirectForm
          actionUrl={redirectData.actionUrl}
          accessCode={redirectData.accessCode}
          encRequest={redirectData.encRequest}
        />
      );
    }

    return (
      <div className="mze-panel">
        <div className="mze-panel__header">
          <Wallet size={16} />
          <span>{i18n.t('add_money_wallet')}</span>
        </div>

        <div className="mze-panel__body">
          {this.renderAmountField()}
          {this.renderPaymentTabs()}

          {paymentMethod === 'offline' && this.renderOfflineForm()}
          {paymentMethod === 'online' && this.renderOnlineForm()}

          <div className="mze-submit-area">{this.renderSubmitButton()}</div>
        </div>
      </div>
    );
  }
}
